import classNames from "classnames";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useOverlays } from "contexts/OverlayContext";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { EditorFocusTarget } from "@components/settings/ChartOfAccountsTemplates/editor/editorBodyTypes";
import { getGroupActionTooltipMessage } from "./groupMutationTargets";
import styles from "./styles.module.scss";

interface Props {
    // The representative constituent, used by the editor opener. Delete reads
    // `similarGroups` instead so it can span every business the merged row
    // stands for.
    group: AccountGroup;
    similarGroups: AccountGroup[];
    openEditorForRow: (target: EditorFocusTarget) => void;
}

export const GroupActions = ({
    group,
    similarGroups,
    openEditorForRow,
}: Readonly<Props>) => {
    const { openOverlay } = useOverlays();
    const canEditCoA = useCanPerformWorkspaceAction(WorkspaceAction.COA_EDIT);
    const tooltipMessage = getGroupActionTooltipMessage({ canEditCoA });
    const isDisabled = tooltipMessage !== undefined;

    const handleEditGroup = () => {
        if (isDisabled) {
            return;
        }
        openEditorForRow({ kind: "group", id: group.id });
    };

    const handleDeleteGroup = () => {
        if (isDisabled) {
            return;
        }
        openOverlay<"deleteCoaGroup">("deleteCoaGroup", {
            name: group.name,
            groups: similarGroups.map((similarGroup) => ({
                id: similarGroup.id,
                entityId: similarGroup.entityId,
            })),
        });
    };

    return (
        <div className={classNames(styles.columnContent, styles.actionsColumn)}>
            <Menu.More className={viewTableStyles.showOnHover}>
                <Menu.Item
                    onClick={handleEditGroup}
                    disabled={isDisabled}
                    disabledReason={tooltipMessage}
                >
                    Edit
                </Menu.Item>
                <Menu.Item
                    onClick={handleDeleteGroup}
                    disabled={isDisabled}
                    disabledReason={tooltipMessage}
                    destructive
                >
                    Delete
                </Menu.Item>
            </Menu.More>
        </div>
    );
};
